import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { mockPurchaseOrderService } from '@/services/mock/MockPurchaseOrderService';
import { mockDocumentCommentService } from '@/services/mock/MockDocumentCommentService';
import { Phase17GrnHandoffPayload, PurchaseOrderRecord, PoActionType } from '@/types/domain/po-management';
import { DocumentAttachment, RequestComment } from '@/types/domain';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import { animateFadeIn } from '@/lib/motion';
import { ROUTES } from '@/constants/routes';
import { usePermission } from '@/hooks/usePermission';
import {
  ArrowLeft,
  FileText,
  MessageSquare,
  ShoppingBag,
  Database,
  Package,
  Layers,
  XCircle,
  Truck,
  PackageCheck,
  CheckCircle,
  AlertTriangle,
  Send,
  Printer,
  History,
  ShieldCheck,
  DollarSign,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import { PoItemsTable } from '@/components/purchase-orders/PoItemsTable';
import { PoDeliveryTracker } from '@/components/purchase-orders/PoDeliveryTracker';
import { ThreeWayMatchPanel } from '@/components/purchase-orders/ThreeWayMatchPanel';
import { PoSapSyncModal } from '@/components/purchase-orders/PoSapSyncModal';
import { DocumentManagementPanel } from '@/components/data-display/DocumentManagementPanel';
import { EnterpriseCommentThread } from '@/components/data-display/EnterpriseCommentThread';
import { PoActionModal } from '@/features/purchase-orders/components/PoActionModal';
import { PoApprovalsTimeline } from '@/features/purchase-orders/components/PoApprovalsTimeline';
import { PoAuditHistoryTab } from '@/features/purchase-orders/components/PoAuditHistoryTab';
import { PoPrintPreviewModal } from '@/features/purchase-orders/components/PoPrintPreviewModal';

type PoDetailTab =
  | 'OVERVIEW'
  | 'ITEMS'
  | 'COMMERCIAL'
  | 'DELIVERY'
  | 'MATCH'
  | 'APPROVALS'
  | 'SAP'
  | 'DOCUMENTS'
  | 'COMMENTS'
  | 'AUDIT';

export interface PurchaseOrderDetailPageProps {
  recordId?: string;
  isDrawer?: boolean;
  onClose?: () => void;
}

export const PurchaseOrderDetailPage: React.FC<PurchaseOrderDetailPageProps> = ({
  recordId,
  isDrawer,
}) => {
  const { id: paramId } = useParams<{ id: string }>();
  const id = recordId || paramId;
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const { can, user } = usePermission();

  const [po, setPo] = useState<PurchaseOrderRecord | null>(null);
  const [documents, setDocuments] = useState<DocumentAttachment[]>([]);
  const [comments, setComments] = useState<RequestComment[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Modals state
  const [isSapModalOpen, setIsSapModalOpen] = useState(false);
  const [isSyncingSap, setIsSyncingSap] = useState(false);
  const [isPrintModalOpen, setIsPrintModalOpen] = useState(false);
  const [isGrnPayloadModalOpen, setIsGrnPayloadModalOpen] = useState(false);
  const [actionModalType, setActionModalType] = useState<PoActionType | null>(null);
  const [grnHandoffPayload, setGrnHandoffPayload] = useState<Phase17GrnHandoffPayload | null>(null);

  const [activeTab, setActiveTab] = useState<PoDetailTab>('OVERVIEW');

  const loadData = async () => {
    if (!id) return;
    try {
      setIsLoading(true);
      const [order, docs, comms] = await Promise.all([
        mockPurchaseOrderService.getPurchaseOrderById(id),
        mockDocumentCommentService.getDocuments('PO', id),
        mockDocumentCommentService.getComments('PO', id),
      ]);

      if (order) {
        setPo(order);
        const grnPayload = await mockPurchaseOrderService.generateGrnHandoffPayload(order.id);
        setGrnHandoffPayload(grnPayload);
      }
      setDocuments(docs);
      setComments(comms);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    animateFadeIn(pageRef.current);
    loadData();
  }, [id]);

  const handleConfirmSapSync = async () => {
    if (!po) return;
    try {
      setIsSyncingSap(true);
      await mockPurchaseOrderService.syncPoWithSap(po.id);
      setIsSapModalOpen(false);
      await loadData();
    } finally {
      setIsSyncingSap(false);
    }
  };

  const handleExecutePoAction = async (payload: { reason?: string; notes?: string }) => {
    if (!po || !actionModalType) return;

    switch (actionModalType) {
      case 'SUBMIT':
        await mockPurchaseOrderService.submitPurchaseOrder(po.id);
        break;
      case 'APPROVE':
        await mockPurchaseOrderService.approvePurchaseOrder(po.id, user?.name || 'Rachel Chen', payload.notes);
        break;
      case 'REJECT':
        await mockPurchaseOrderService.rejectPurchaseOrder(po.id, payload.reason || 'Rejected by authorized approver');
        break;
      case 'SEND_BACK':
        await mockPurchaseOrderService.sendBackPurchaseOrder(po.id, payload.reason || 'Revision required by approver');
        break;
      case 'ISSUE':
        await mockPurchaseOrderService.issuePurchaseOrder(po.id);
        break;
      case 'ACKNOWLEDGE':
        await mockPurchaseOrderService.acknowledgePurchaseOrder(po.id);
        break;
      case 'CANCEL':
        await mockPurchaseOrderService.cancelPo(po.id, payload.reason);
        break;
      case 'CLOSE':
        await mockPurchaseOrderService.closePo(po.id, payload.reason);
        break;
      case 'AMEND':
        await mockPurchaseOrderService.amendPurchaseOrder(po.id, {}, payload.reason || 'Contractual amendment');
        break;
      default:
        break;
    }

    await loadData();
  };

  if (isLoading) {
    return (
      <div className="p-16 text-center text-xs text-foreground-secondary font-medium font-poppins">
        <ShoppingBag className="h-8 w-8 animate-bounce mx-auto text-primary mb-2" />
        Loading Enterprise Purchase Order Command Center...
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-16 text-center space-y-3 font-poppins">
        <h2 className="text-lg font-extrabold text-foreground">Purchase Order Not Found</h2>
        <p className="text-xs text-foreground-secondary">The requested PO does not exist or has been archived.</p>
        <Button onClick={() => navigate(ROUTES.PURCHASE_ORDERS.LIST)} className="rounded-xl font-bold font-poppins cursor-pointer">
          Return to PO Control Center
        </Button>
      </div>
    );
  }

  return (
    <div ref={pageRef} className={`space-y-4 font-poppins ${isDrawer ? 'pb-2' : 'pb-24 max-w-7xl mx-auto'}`}>
      {/* Top Breadcrumb & Action Bar */}
      <div className={`flex justify-between gap-4 ${isDrawer ? 'flex-col items-start' : 'flex-col sm:flex-row sm:items-center'}`}>
        <div className="flex items-center gap-3">
          {!isDrawer && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(ROUTES.PURCHASE_ORDERS.LIST)}
              className="h-9 rounded-xl border-border font-bold text-xs font-poppins cursor-pointer"
            >
              <ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Purchase Orders
            </Button>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xl font-black text-foreground tracking-tight font-poppins">{po.poNumber}</span>
            <span className="text-xs font-mono font-bold text-slate-500 bg-surface px-2 py-0.5 rounded border border-border">
              Rev {po.version}
            </span>
            {po.sapPoNumber && (
              <span className="font-mono text-xs font-bold text-purple-600 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20 flex items-center gap-1">
                <Database className="h-3 w-3" /> SAP: {po.sapPoNumber}
              </span>
            )}
            {po.isDelayed && (
              <span className="text-xs font-bold text-rose-600 bg-rose-500/10 px-2 py-0.5 rounded border border-rose-500/20 flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Delay Exception ({po.delayDays}d overdue)
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setIsPrintModalOpen(true)}
            className="rounded-xl font-bold border-border text-xs font-poppins cursor-pointer"
          >
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Print / Export PO
          </Button>

          {po.status === 'DRAFT' && can('PO_CREATE') && (
            <Button
              size="sm"
              onClick={() => setActionModalType('SUBMIT')}
              className="rounded-xl font-bold bg-primary text-white shadow-2xs text-xs"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Submit for Approval
            </Button>
          )}

          {po.status === 'PENDING_RELEASE' && can('PO_APPROVE') && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActionModalType('SEND_BACK')}
                className="rounded-xl font-bold border-border text-xs"
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5 text-amber-600" /> Send Back
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setActionModalType('REJECT')}
                className="rounded-xl font-bold border-rose-500/30 text-rose-600 hover:bg-rose-50 text-xs"
              >
                <XCircle className="h-3.5 w-3.5 mr-1.5" /> Reject
              </Button>
              <Button
                size="sm"
                onClick={() => setActionModalType('APPROVE')}
                className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs text-xs"
              >
                <CheckCircle className="h-3.5 w-3.5 mr-1.5" /> Authorize & Release
              </Button>
            </>
          )}

          {po.status === 'RELEASED' && can('PO_CREATE') && (
            <Button
              size="sm"
              onClick={() => setActionModalType('ISSUE')}
              className="rounded-xl font-bold bg-primary hover:bg-primary/90 text-white shadow-2xs text-xs"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" /> Send to Supplier
            </Button>
          )}

          {po.status === 'SENT_TO_SUPPLIER' && (
            <Button
              size="sm"
              onClick={() => setActionModalType('ACKNOWLEDGE')}
              className="rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white shadow-2xs text-xs"
            >
              <CheckCircle className="h-3.5 w-3.5 mr-1.5" /> Supplier Acknowledged
            </Button>
          )}

          {po.sapStatus !== 'SYNCED' && po.status !== 'CANCELLED' && po.status !== 'CLOSED' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSapModalOpen(true)}
              className="rounded-xl font-bold border-purple-500/30 bg-purple-500/5 text-purple-700 hover:bg-purple-500/10 text-xs"
            >
              <Database className="h-3.5 w-3.5 mr-1.5" /> Sync SAP ME21N
            </Button>
          )}

          {po.status !== 'CANCELLED' && po.status !== 'CLOSED' && po.status !== 'DELIVERED' && can('PO_CREATE') && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setActionModalType('CANCEL')}
              className="rounded-xl font-bold text-rose-600 hover:bg-rose-50 text-xs"
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" /> Cancel PO
            </Button>
          )}
        </div>
      </div>

      {/* Main Command Center Header Card */}
      <Card className="rounded-2xl border-border bg-card shadow-2xs overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-border/60 pb-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-extrabold uppercase tracking-wider text-primary">
                  Commercial Purchase Commitment
                </span>
                <span
                  className={`text-[10px] font-extrabold uppercase px-2.5 py-0.5 rounded-full border ${
                    po.status === 'RELEASED' || po.status === 'SENT_TO_SUPPLIER' || po.status === 'DELIVERED'
                      ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                      : po.status === 'CANCELLED'
                      ? 'bg-rose-500/10 text-rose-700 border-rose-500/20'
                      : 'bg-amber-500/10 text-amber-700 border-amber-500/20'
                  }`}
                >
                  {po.status}
                </span>
              </div>
              <h2 className="text-xl font-black text-foreground">{po.vendorName}</h2>
              <p className="text-xs text-foreground-secondary">
                Vendor Code: <span className="font-mono font-bold text-foreground">{po.vendorCode}</span> | Plant: {po.plant} | Buyer: {po.buyer}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-4 text-right">
              <div className="p-3 rounded-xl bg-surface border border-border/70 text-left sm:text-right">
                <span className="text-[10px] font-bold text-foreground-secondary uppercase block">Total PO Value</span>
                <span className="text-xl font-black text-primary font-mono">{formatCurrency(po.totalValue, po.currency)}</span>
                <span className="text-[10px] text-foreground-muted block">Subtotal: {formatCurrency(po.subtotal, po.currency)} + Tax</span>
              </div>
            </div>
          </div>

          {/* Traceability Grid */}
          <div className={`grid gap-2.5 text-xs ${isDrawer ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-4'}`}>
            <div
              onClick={() => navigate(ROUTES.REQUESTS.DETAILS(po.prId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>1. Source Requisition</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-foreground block">{po.sourcePrNumber || po.prId}</span>
              <span className="text-[10px] text-emerald-600 font-semibold">Approved Requisition</span>
            </div>

            <div
              onClick={() => navigate(ROUTES.RFQ.DETAILS(po.rfqId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>2. Source RFQ</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-foreground block">{po.sourceRfqNumber || po.rfqId}</span>
              <span className="text-[10px] text-purple-600 font-semibold">Sourcing Package</span>
            </div>

            <div
              onClick={() => navigate(ROUTES.QUOTATIONS.COMPARISON(po.rfqId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>3. Selected Quotation</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-foreground block">{po.quotationId}</span>
              <span className="text-[10px] text-blue-600 font-semibold">Evaluated Winning Bid</span>
            </div>

            <div
              onClick={() => navigate(ROUTES.VENDORS.DETAILS(po.vendorId))}
              className="p-3 rounded-xl bg-surface hover:bg-surface/80 border border-border cursor-pointer transition-colors space-y-1"
            >
              <span className="text-[10px] font-bold text-foreground-muted uppercase block flex items-center justify-between">
                <span>4. Awarded Vendor</span>
                <ExternalLink className="h-3 w-3" />
              </span>
              <span className="font-bold text-foreground block truncate">{po.vendorName}</span>
              <span className="text-[10px] text-teal-600 font-semibold">Active Master Record</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Navigation Tabs */}
      <div className="flex items-center gap-1.5 p-1 rounded-2xl bg-muted/40 border border-border/80 overflow-x-auto custom-scrollbar no-scrollbar text-xs">
        {(
          [
            { id: 'OVERVIEW', label: 'Overview & Traceability', icon: FileText },
            { id: 'ITEMS', label: `Line Items (${po.items.length})`, icon: Package },
            { id: 'COMMERCIAL', label: 'Commercial & Taxes', icon: DollarSign },
            { id: 'DELIVERY', label: 'Delivery & GRN Tracker', icon: Truck },
            { id: 'MATCH', label: '3-Way Match', icon: Layers },
            { id: 'APPROVALS', label: 'Approvals & Governance', icon: ShieldCheck },
            { id: 'SAP', label: 'SAP BAPI ME21N', icon: Database },
            { id: 'DOCUMENTS', label: `Documents (${documents.length})`, icon: FileText },
            { id: 'COMMENTS', label: `Comments (${comments.length})`, icon: MessageSquare },
            { id: 'AUDIT', label: 'Audit Log', icon: History },
          ] as const
        ).map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-3.5 py-2 rounded-xl whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-primary/10 text-primary font-black border border-primary/20 shadow-2xs'
                  : 'text-foreground-secondary hover:text-foreground font-bold hover:bg-surface'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Tab Content 1: OVERVIEW */}
      {activeTab === 'OVERVIEW' && (
        <div className={isDrawer ? 'flex flex-col gap-4' : 'grid grid-cols-1 md:grid-cols-3 gap-6'}>
          <Card className={`${isDrawer ? 'w-full' : 'md:col-span-2'} rounded-2xl border-border`}>
            <CardContent className="p-6 space-y-6">
              <div className="border-b border-border/60 pb-3">
                <h3 className="text-sm font-extrabold text-foreground">Purchase Order Executive Summary</h3>
                <p className="text-xs text-foreground-secondary">
                  High-level commercial terms, promised fulfillment timelines, and destination routing.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Purchasing Organization:</span>
                  <p className="font-extrabold text-foreground">{po.purchasingOrganization} (Global Procurement Org)</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Purchasing Group:</span>
                  <p className="font-extrabold text-foreground">{po.purchasingGroup}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Payment Terms:</span>
                  <p className="font-extrabold text-foreground">{po.paymentTerms}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Incoterms:</span>
                  <p className="font-extrabold text-foreground">{po.incoterms} — Delivered Duty Paid</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Delivery Destination:</span>
                  <p className="font-medium text-foreground">{po.shippingAddress}</p>
                </div>

                <div className="space-y-1">
                  <span className="text-foreground-secondary font-medium">Billing Address:</span>
                  <p className="font-medium text-foreground">{po.billingAddress}</p>
                </div>

                <div className="sm:col-span-2 space-y-1 pt-2 border-t border-border/40">
                  <span className="text-foreground-secondary font-medium">Instructions & Notes:</span>
                  <p className="text-foreground font-medium bg-surface p-3 rounded-xl border border-border/60">
                    {po.notes || 'No special shipping instructions provided.'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-4">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Truck className="h-4 w-4 text-primary" /> Phase 17 GRN Handoff Ready
                </h3>
                <p className="text-xs text-foreground-secondary">
                  This PO structure exports an authoritative fulfillment contract for upcoming Goods Receipt Note (GRN) processing.
                </p>
                <div className="space-y-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      navigate(ROUTES.GRN.NEW, {
                        state: {
                          poHandoffPayload: grnHandoffPayload,
                          poId: po.id,
                          poNumber: po.poNumber,
                        },
                      })
                    }
                    className="w-full rounded-xl font-bold bg-emerald-600 hover:bg-emerald-700 text-white text-xs shadow-2xs"
                  >
                    <PackageCheck className="h-4 w-4 mr-1.5" /> Receive Goods (Create GRN)
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setIsGrnPayloadModalOpen(true)}
                    className="w-full rounded-xl font-bold text-foreground text-xs border-border"
                  >
                    Inspect Phase 17 GRN Handoff Payload
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-2xl border-border">
              <CardContent className="p-6 space-y-3 text-xs">
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> Source of Truth
                </h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Commercial Total:</span>
                    <span className="font-bold text-foreground">ProcureAI Transaction</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">SAP Document:</span>
                    <span className="font-bold text-purple-700">SAP S/4HANA (BAPI)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Supplier Award:</span>
                    <span className="font-bold text-foreground">RFQ Quotation Matrix</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Tab Content 2: ITEMS */}
      {activeTab === 'ITEMS' && <PoItemsTable items={po.items} currency={po.currency} />}

      {/* Tab Content 3: COMMERCIAL */}
      {activeTab === 'COMMERCIAL' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="border-b border-border/60 pb-3">
              <h3 className="text-sm font-extrabold text-foreground">Commercial Commitment & Tax Calculation</h3>
              <p className="text-xs text-foreground-secondary">
                Itemized financial reconciliation under enterprise accounting rules.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="space-y-3 text-xs">
                <span className="text-xs font-extrabold text-foreground uppercase tracking-wider block">
                  Contractual Terms
                </span>
                <div className="p-4 rounded-xl border border-border bg-surface space-y-2.5">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Currency:</span>
                    <span className="font-bold font-mono">{po.currency}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Payment Terms:</span>
                    <span className="font-bold">{po.paymentTerms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Incoterms:</span>
                    <span className="font-bold">{po.incoterms}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Freight & Logistics:</span>
                    <span className="font-bold">{po.deliveryTerms}</span>
                  </div>
                </div>
              </div>

              <div className="space-y-3 text-xs">
                <span className="text-xs font-extrabold text-foreground uppercase tracking-wider block">
                  Financial Breakdown
                </span>
                <div className="p-4 rounded-xl border border-border bg-card space-y-3">
                  <div className="flex justify-between text-foreground-secondary">
                    <span>Base Subtotal ({po.items.length} lines):</span>
                    <span className="font-mono font-bold text-foreground">{formatCurrency(po.subtotal, po.currency)}</span>
                  </div>
                  {po.discount > 0 && (
                    <div className="flex justify-between text-emerald-600 font-bold">
                      <span>Contractual Discount:</span>
                      <span className="font-mono">- {formatCurrency(po.discount, po.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-foreground-secondary">
                    <span>Applicable Taxes & Levies:</span>
                    <span className="font-mono font-bold text-foreground">{formatCurrency(po.tax, po.currency)}</span>
                  </div>
                  {po.freight > 0 && (
                    <div className="flex justify-between text-foreground-secondary">
                      <span>Freight & Shipping:</span>
                      <span className="font-mono font-bold text-foreground">{formatCurrency(po.freight, po.currency)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-3 border-t border-border text-base font-black text-foreground">
                    <span>Grand Total Commitment:</span>
                    <span className="font-mono text-primary">{formatCurrency(po.totalValue, po.currency)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 4: DELIVERY */}
      {activeTab === 'DELIVERY' && <PoDeliveryTracker po={po} />}

      {/* Tab Content 5: MATCH */}
      {activeTab === 'MATCH' && <ThreeWayMatchPanel po={po} />}

      {/* Tab Content 6: APPROVALS */}
      {activeTab === 'APPROVALS' && (
        <PoApprovalsTimeline approvals={po.approvals} poCurrency={po.currency} />
      )}

      {/* Tab Content 7: SAP */}
      {activeTab === 'SAP' && (
        <Card className="rounded-2xl border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border/60 pb-3">
              <div>
                <h3 className="text-sm font-extrabold text-foreground flex items-center gap-2">
                  <Database className="h-4 w-4 text-purple-600" /> SAP S/4HANA BAPI ME21N Integration
                </h3>
                <p className="text-xs text-foreground-secondary">
                  Bi-directional replication of Purchase Order to SAP ERP master instance.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <span
                  className={`text-xs font-extrabold px-3 py-1 rounded-full border ${
                    po.sapStatus === 'SYNCED'
                      ? 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20'
                      : 'bg-amber-500/10 text-amber-700 border-amber-500/20'
                  }`}
                >
                  SAP Status: {po.sapStatus}
                </span>

                {po.sapStatus !== 'SYNCED' && (
                  <Button
                    size="sm"
                    onClick={() => setIsSapModalOpen(true)}
                    className="rounded-xl font-bold bg-purple-600 hover:bg-purple-700 text-white shadow-2xs text-xs"
                  >
                    Sync Now
                  </Button>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP Document Number</span>
                <p className="font-mono text-base font-black text-purple-700">{po.sapPoNumber || 'PENDING GENERATION'}</p>
                <span className="text-[10px] text-foreground-muted">Standard 10-digit SAP PO document</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">SAP PR Reference</span>
                <p className="font-mono text-base font-black text-foreground">{po.sapPrNumber || '45000123'}</p>
                <span className="text-[10px] text-foreground-muted">Linked upstream PR document</span>
              </div>

              <div className="p-4 rounded-xl border border-border bg-surface space-y-1">
                <span className="text-foreground-secondary font-bold uppercase text-[10px]">ERP Target Plant</span>
                <p className="font-mono text-base font-black text-foreground">{po.plant}</p>
                <span className="text-[10px] text-foreground-muted">Configured SAP Plant code</span>
              </div>
            </div>

            <div className="p-4 rounded-xl border border-border/80 bg-slate-900 text-slate-100 font-mono text-xs overflow-x-auto space-y-2">
              <span className="text-slate-400 text-[10px] uppercase font-bold block">// BAPI_PO_CREATE1 Payload Schema Definition</span>
              <pre className="text-[11px] leading-relaxed">
{JSON.stringify(
  {
    POHEADER: {
      COMP_CODE: po.companyCode,
      DOC_TYPE: 'NB',
      PURCH_ORG: po.purchasingOrganization,
      PUR_GROUP: po.purchasingGroup,
      VENDOR: po.vendorCode,
      PMNTTRMS: po.paymentTerms,
    },
    POITEM: po.items.map((it) => ({
      PO_ITEM: it.lineNumber,
      MATERIAL: it.materialCode,
      SHORT_TEXT: it.description,
      PLANT: it.plant,
      STGE_LOC: it.storageLocation,
      QUANTITY: it.quantityOrdered,
      PO_UNIT: it.uom,
      NET_PRICE: it.unitPrice,
      TAX_CODE: `${it.taxPercent}%`,
    })),
  },
  null,
  2
)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab Content 8: DOCUMENTS */}
      {activeTab === 'DOCUMENTS' && (
        <DocumentManagementPanel
          documents={documents}
          onUploadFile={async (file, category) => {
            if (!po) return;
            const newDoc = await mockDocumentCommentService.uploadDocument('PO', po.id, file, category);
            setDocuments((prev) => [newDoc, ...prev]);
          }}
          onDownloadFile={async (docId) => {
            await mockDocumentCommentService.downloadDocument(docId);
          }}
          onDeleteFile={async (docId) => {
            await mockDocumentCommentService.deleteDocument(docId);
            setDocuments((prev) => prev.filter((d) => d.id !== docId));
          }}
        />
      )}

      {/* Tab Content 9: COMMENTS */}
      {activeTab === 'COMMENTS' && (
        <EnterpriseCommentThread
          comments={comments}
          onAddComment={async (text, attachmentIds) => {
            if (!po) return;
            const newComment = await mockDocumentCommentService.createComment(
              'PO',
              po.id,
              text,
              attachmentIds
            );
            setComments((prev) => [...prev, newComment]);
          }}
          onDeleteComment={async (commentId) => {
            await mockDocumentCommentService.deleteComment(commentId);
            setComments((prev) => prev.filter((c) => c.id !== commentId));
          }}
        />
      )}

      {/* Tab Content 10: AUDIT */}
      {activeTab === 'AUDIT' && <PoAuditHistoryTab auditHistory={po.auditHistory} />}

      {/* Modals */}
      <PoActionModal
        isOpen={Boolean(actionModalType)}
        onClose={() => setActionModalType(null)}
        actionType={actionModalType}
        po={po}
        onConfirm={handleExecutePoAction}
      />

      <PoSapSyncModal
        isOpen={isSapModalOpen}
        onClose={() => setIsSapModalOpen(false)}
        onConfirmSync={handleConfirmSapSync}
        po={po}
        isSubmitting={isSyncingSap}
      />

      <PoPrintPreviewModal
        isOpen={isPrintModalOpen}
        onClose={() => setIsPrintModalOpen(false)}
        po={po}
      />

      {/* Phase 17 GRN Handoff Payload Drawer Modal */}
      {isGrnPayloadModalOpen && grnHandoffPayload && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl max-w-2xl w-full p-6 space-y-4 shadow-2xl">
            <div className="flex items-center justify-between border-b border-border/60 pb-3">
              <h3 className="text-base font-extrabold text-foreground flex items-center gap-2">
                <Truck className="h-5 w-5 text-primary" /> Phase 17 GRN Handoff Contract
              </h3>
              <Button size="sm" variant="ghost" onClick={() => setIsGrnPayloadModalOpen(false)}>
                Close
              </Button>
            </div>
            <p className="text-xs text-foreground-secondary">
              This payload contract will be consumed by Phase 17 (Goods Receipt Notes) to register physical receipts against PO line items.
            </p>
            <div className="bg-slate-900 text-slate-100 p-4 rounded-xl font-mono text-xs max-h-80 overflow-y-auto">
              <pre>{JSON.stringify(grnHandoffPayload, null, 2)}</pre>
            </div>
            <div className="flex justify-end pt-2">
              <Button size="sm" onClick={() => setIsGrnPayloadModalOpen(false)} className="rounded-xl font-bold">
                Done
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
